/**
 * VoiceControls — presentational buttons for voice dictation in the chat
 * composer, driven by a {@link VoiceDictation} object from `useVoiceDictation`.
 *
 * - `VoiceControls.Mic`: the mic (start) button + a small dropdown to pick the
 *   voice language before speaking. Shown when the input is empty.
 * - `VoiceControls.Listening`: the in-progress controls — a listening indicator,
 *   cancel (✗) and confirm (✓). Shown while dictating.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Mic, X } from 'lucide-react';
import { Button } from '../atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../atoms/dropdown-menu';
import type { VoiceDictation } from './useVoiceDictation';

interface VoiceMicProps {
  voice: VoiceDictation;
  disabled?: boolean;
}

const VoiceMic: React.FC<VoiceMicProps> = ({ voice, disabled }) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center me-1">
    <Button
      variant="tertiary-gray"
      size="sm"
      onClick={voice.begin}
      disabled={disabled}
      aria-label={t('composer.voice.start')}
      title={t('composer.voice.speakToType')}
      className="p-1 w-8 h-8 rounded-xl hover:bg-blue-light-50 hover:text-blue-600 disabled:opacity-50"
      data-testid="chat-voice-button"
    >
      <Mic size={18} strokeWidth={2} />
    </Button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${t('composer.voice.language')}: ${
            voice.lang === 'ar' ? 'العربية' : 'English'
          }`}
          title={t('composer.voice.language')}
          className="flex h-8 w-5 items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 disabled:opacity-50"
          data-testid="chat-voice-lang"
        >
          <ChevronDown size={14} strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="min-w-[9rem]">
        <DropdownMenuLabel>{t('composer.voice.language')}</DropdownMenuLabel>
        <DropdownMenuItem
          className="gap-2"
          onSelect={() => voice.setLang('en')}
          data-testid="voice-lang-en"
        >
          English
          {voice.lang === 'en' && <Check size={14} className="ms-auto" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          onSelect={() => voice.setLang('ar')}
          data-testid="voice-lang-ar"
        >
          العربية
          {voice.lang === 'ar' && <Check size={14} className="ms-auto" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
  );
};

interface VoiceListeningProps {
  voice: VoiceDictation;
}

const VoiceListening: React.FC<VoiceListeningProps> = ({ voice }) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center gap-1" data-testid="chat-voice-controls">
    <span className="flex items-center gap-0.5 px-1 text-gray-400" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
    <Button
      variant="tertiary-gray"
      size="sm"
      onClick={voice.cancel}
      aria-label={t('composer.voice.cancelInput')}
      title={t('composer.voice.cancel')}
      className="p-1 w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
      data-testid="chat-voice-cancel"
    >
      <X size={16} strokeWidth={2} />
    </Button>
    <Button
      variant="tertiary-gray"
      size="sm"
      onClick={voice.confirm}
      aria-label={t('composer.voice.done')}
      title={t('composer.voice.done')}
      className="p-1 w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
      data-testid="chat-voice-confirm"
    >
      <Check size={16} strokeWidth={2} />
    </Button>
  </div>
  );
};

export const VoiceControls = {
  Mic: VoiceMic,
  Listening: VoiceListening,
};

export default VoiceControls;
